import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { currencyRateService } from '@/services/currencyRateService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencyInput } from '@/components/ui/currency-input';
import { DollarSign, Calculator, AlertTriangle, TrendingUp, RefreshCw, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type CurrencyCode = 'USD' | 'UAH' | 'EUR';

interface CurrencyQuickPanelProps {
  renderTrigger?: (controls: {
    open: () => void;
    openForCurrency: (currency?: CurrencyCode) => void;
    isOpen: boolean;
    badgeVariant: React.ComponentProps<typeof Button>['variant'];
    badgeText: string;
    hasAnyRate: boolean;
    hasBothRates: boolean;
  }) => React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type RateMap = Record<string, { rate: number; validUntil?: string; dateSet: string; updatedAt?: string }>

// Small helper to format
const fmt = (n: number) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n)

const currencyOptions: CurrencyCode[] = ['USD', 'UAH', 'EUR'];

export const CurrencyQuickPanel: React.FC<CurrencyQuickPanelProps> = ({
  renderTrigger,
  open: controlledOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'ADMIN';
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const dialogOpen = isControlled ? controlledOpen! : internalOpen;
  const [pendingEditCurrency, setPendingEditCurrency] = React.useState<CurrencyCode | null>(null);

  const handleOpenChange = React.useCallback((next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
    if (!next) {
      setPendingEditCurrency(null);
    }
  }, [isControlled, onOpenChange]);

  const openDialog = React.useCallback(() => {
    handleOpenChange(true);
  }, [handleOpenChange]);

  const openDialogForCurrency = React.useCallback((currency?: CurrencyCode) => {
    if (currency) {
      setPendingEditCurrency(currency);
    }
    handleOpenChange(true);
  }, [handleOpenChange]);

  const [from, setFrom] = React.useState<CurrencyCode>('USD');
  const [to, setTo] = React.useState<CurrencyCode>('UAH');

  // Helper function to get available currencies for 'to' selection
  const getAvailableToCurrencies = () => {
    return currencyOptions.filter(c => c !== from);
  };
  const [amount, setAmount] = React.useState<string>('1');

  // In-place editing state
  const [editingCurrency, setEditingCurrency] = React.useState<CurrencyCode | null>(null);
  const [editingValue, setEditingValue] = React.useState<number>(0);

  const { data: todayRates, isError } = useQuery({
    queryKey: ['currency', 'today-all'],
    queryFn: () => currencyRateService.getTodayAllRates(),
    refetchInterval: 2 * 60 * 1000,
  });

  // Mutation for setting currency rates
  const setRateMutation = useMutation({
    mutationFn: ({ currency, rate }: { currency: string; rate: number }) =>
      currencyRateService.setTodaysRate(currency, rate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currency', 'today-all'] });
      setEditingCurrency(null);
      toast({
        title: "Success",
        description: "Exchange rate updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update exchange rate",
        variant: "destructive",
      });
    },
  });

  // Build map for quick lookups (rates are USD -> CURRENCY per backend model)
  const rateMap: RateMap = React.useMemo(() => {
    const m: RateMap = {};
    (todayRates || []).forEach(r => {
      m[r.currency.toUpperCase()] = { rate: r.rate, validUntil: r.validUntil, dateSet: r.dateSet, updatedAt: r.updatedAt } as any;
    });
    return m;
  }, [todayRates]);

  const uahRate = rateMap['UAH']?.rate; // 1 USD = x UAH
  const eurRate = rateMap['EUR']?.rate; // 1 USD = x EUR
  const uahValid = !!rateMap['UAH'] && (!rateMap['UAH'].validUntil || new Date(rateMap['UAH'].validUntil!) > new Date());
  const eurValid = !!rateMap['EUR'] && (!rateMap['EUR'].validUntil || new Date(rateMap['EUR'].validUntil!) > new Date());

  // In-place editing functions
  const startEditing = React.useCallback((currency: CurrencyCode, currentRate?: number) => {
    setEditingCurrency(currency);
    setEditingValue(currentRate ?? 0);
  }, []);

  React.useEffect(() => {
    if (!dialogOpen || !pendingEditCurrency) return;
    const currentRate = rateMap[pendingEditCurrency]?.rate;
    startEditing(pendingEditCurrency, currentRate);
    setPendingEditCurrency(null);
  }, [dialogOpen, pendingEditCurrency, rateMap, startEditing]);

  const cancelEditing = () => {
    setEditingCurrency(null);
    setEditingValue(0);
  };

  const saveRate = () => {
    if (editingCurrency && editingValue > 0) {
      setRateMutation.mutate({ currency: editingCurrency, rate: editingValue });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveRate();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Cross currency conversion using USD/UAH/EUR via UAH pivot
  const calc = React.useMemo(() => {
    const a = parseFloat(amount || '0');
    if (!isFinite(a) || a <= 0) return { result: 0 };
    if (from === to) return { result: a };
    const usdToUah = rateMap['UAH']?.rate; // UAH per 1 USD
    const usdToEur = rateMap['EUR']?.rate; // EUR per 1 USD

    const toUSD = (cur: 'USD'|'UAH'|'EUR', value: number): number => {
      if (cur === 'USD') return value;
      if (cur === 'UAH' && usdToUah) return value / usdToUah;
      if (cur === 'EUR' && usdToEur) return value / usdToEur;
      return NaN;
    };
    const fromUSDTo = (cur: 'USD'|'UAH'|'EUR', usdVal: number): number => {
      if (cur === 'USD') return usdVal;
      if (cur === 'UAH' && usdToUah) return usdVal * usdToUah;
      if (cur === 'EUR' && usdToEur) return usdVal * usdToEur;
      return NaN;
    };

    const inUsd = toUSD(from, a);
    if (!isFinite(inUsd)) return { result: 0 };
    const res = fromUSDTo(to, inUsd);
    return { result: isFinite(res) ? res : 0 };
  }, [amount, from, to, rateMap]);

  // Top bar badge - red when no rates, yellow when partial, green when both set
  const hasAnyRate = uahValid || eurValid;
  const hasBothRates = uahValid && eurValid;

  const badgeVariant = isError ? 'destructive' : (!hasAnyRate ? 'destructive' : (!hasBothRates ? 'default' : 'secondary'));
  const badgeText = isError ? 'Currency service unavailable' : (uahValid && uahRate ? `1 USD = ${fmt(uahRate)} UAH` : 'Set Exchange Rates');

  const triggerContent = renderTrigger
    ? renderTrigger({
        open: openDialog,
        openForCurrency: openDialogForCurrency,
        isOpen: dialogOpen,
        badgeVariant,
        badgeText,
        hasAnyRate,
        hasBothRates,
      })
    : (
        <Button
          variant={badgeVariant}
          size="sm"
          onClick={openDialog}
          className="h-8 gap-2 font-medium"
        >
          <TrendingUp className="h-3.5 w-3.5" />
          {badgeText}
        </Button>
      );

  return (
    <>
      {triggerContent}
      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-auto">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Calculator className="h-5 w-5 text-primary" />
              </div>
              {t('common.currencyExchange')}
            </DialogTitle>
            <DialogDescription>
              {t('currency.dialogDescription', 'Manage exchange rates and convert between currencies')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
              {/* Modern Rate Cards with In-Place Editing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* USD → UAH Rate Card */}
                <Card className={cn(
                  "relative overflow-hidden transition-all duration-300",
                  editingCurrency === 'UAH' && "ring-2 ring-primary border-transparent shadow-lg"
                )}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium">{t('common.usdToUah')}</CardTitle>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 opacity-60 hover:opacity-100 relative z-10 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing('UAH', uahRate);
                          }}
                          disabled={editingCurrency !== null}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {editingCurrency === 'UAH' ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CurrencyInput
                            value={editingValue}
                            onValueChange={setEditingValue}
                            placeholder="33.5000"
                            maxDecimalPlaces={4}
                            maxIntegerPlaces={2}
                            className="text-2xl font-bold h-12 bg-primary/5 border-primary/20 focus:border-primary"
                            onKeyDown={handleKeyDown}
                            autoFocus
                          />
                          <span className="text-muted-foreground font-medium">UAH</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={saveRate}
                            disabled={setRateMutation.isPending || editingValue <= 0}
                            className="h-8 px-3"
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditing}
                            disabled={setRateMutation.isPending}
                            className="h-8 px-3"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold">
                            {uahRate ? fmt(uahRate) : '—.—'}
                          </span>
                          <span className="text-muted-foreground">UAH</span>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          {rateMap['UAH']?.updatedAt ? (() => {
                            const date = new Date(rateMap['UAH'].updatedAt);
                            const hours = date.getHours().toString().padStart(2, '0');
                            const minutes = date.getMinutes().toString().padStart(2, '0');
                            const day = date.getDate().toString().padStart(2, '0');
                            const month = (date.getMonth() + 1).toString().padStart(2, '0');
                            const year = date.getFullYear().toString().slice(-2);
                            return `Updated: ${hours}:${minutes} - ${day}.${month}.${year}`;
                          })() : 'Not set'}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* USD → EUR Rate Card */}
                <Card className={cn(
                  "relative overflow-hidden transition-all duration-300",
                  editingCurrency === 'EUR' && "ring-2 ring-primary border-transparent shadow-lg"
                )}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium">{t('common.usdToEur')}</CardTitle>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 opacity-60 hover:opacity-100 relative z-10 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing('EUR', eurRate);
                          }}
                          disabled={editingCurrency !== null}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {editingCurrency === 'EUR' ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CurrencyInput
                            value={editingValue}
                            onValueChange={setEditingValue}
                            placeholder="0.9000"
                            maxDecimalPlaces={4}
                            maxIntegerPlaces={2}
                            className="text-2xl font-bold h-12 bg-primary/5 border-primary/20 focus:border-primary"
                            onKeyDown={handleKeyDown}
                            autoFocus
                          />
                          <span className="text-muted-foreground font-medium">EUR</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={saveRate}
                            disabled={setRateMutation.isPending || editingValue <= 0}
                            className="h-8 px-3"
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditing}
                            disabled={setRateMutation.isPending}
                            className="h-8 px-3"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold">
                            {eurRate ? fmt(eurRate) : '—.—'}
                          </span>
                          <span className="text-muted-foreground">EUR</span>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          {rateMap['EUR']?.updatedAt ? (() => {
                            const date = new Date(rateMap['EUR'].updatedAt);
                            const hours = date.getHours().toString().padStart(2, '0');
                            const minutes = date.getMinutes().toString().padStart(2, '0');
                            const day = date.getDate().toString().padStart(2, '0');
                            const month = (date.getMonth() + 1).toString().padStart(2, '0');
                            const year = date.getFullYear().toString().slice(-2);
                            return `Updated: ${hours}:${minutes} - ${day}.${month}.${year}`;
                          })() : 'Not set'}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Warning Alert - When no rates set or incomplete */}
              {!hasBothRates && (
                <Alert variant={!hasAnyRate ? "destructive" : "default"}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {!hasAnyRate
                      ? "Exchange rates are needed for currency conversions. Click the refresh icons above to set current rates."
                      : "Complete your exchange rate setup. Set both USD→UAH and USD→EUR rates for full currency conversion support."
                    }
                  </AlertDescription>
                </Alert>
              )}

              {/* Admin Help Text */}
              {isAdmin && (
                <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  💡 <strong>{t('common.adminTipRefresh')}</strong>
                </div>
              )}
              {/* Currency Converter */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('common.quickCurrencyConverter')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Currency Selection */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t('common.fromCurrency')}</Label>
                      <div className="flex gap-2">
                        {currencyOptions.map((c) => (
                          <Button
                            key={`from-${c}`}
                            size="sm"
                            className="h-9 px-4"
                            variant={from === c ? 'default' : 'outline'}
                            onClick={() => {
                              setFrom(c);
                              // If setting from currency same as to currency, switch to currency
                              if (c === to) {
                                const alternatives = getAvailableToCurrencies();
                                setTo(alternatives[0]);
                              }
                            }}
                          >
                            {c}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t('common.toCurrency')}</Label>
                      <div className="flex gap-2">
                        {currencyOptions.map((c) => (
                          <Button
                            key={`to-${c}`}
                            size="sm"
                            variant={to === c ? 'default' : 'outline'}
                            disabled={from === c}
                            className={cn(
                              'h-9 px-4',
                              from === c && 'opacity-40 cursor-not-allowed'
                            )}
                            onClick={() => {
                              if (from === c) return; // Prevent click if disabled
                              setTo(c);
                            }}
                          >
                            {c}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Amount Input and Result */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('common.amountLabel', { currency: from })}</Label>
                      <Input
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Enter amount"
                        className="h-12 text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('common.resultLabel', { currency: to })}</Label>
                      <div className="h-12 px-3 py-2 border rounded-md bg-muted/30 flex items-center">
                        <span className="text-lg font-semibold text-foreground">
                          {fmt(calc.result)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Exchange Rate Display */}
                  {from !== to && calc.result > 0 && (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                      <div className="text-lg font-medium">
                        1 {from} = {fmt(calc.result / parseFloat(amount || '1'))} {to}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default CurrencyQuickPanel;
